import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Layers,
  Search,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import '../../styles/module-shared.css';
import apiService from '../../utils/apiService';
import useAccountTier from '../accountTier/useAccountTier';
import {
  mapInstrumentCommissionGroups,
  mapPartnerTiersToTabs,
} from './forexGroupsData';
import './instruments-commission.css';

const THEME = getThemeColor('primary');

const InstrumentsCommission = () => {
  const { loading, error, tiers, currentTier, userTier } = useAccountTier('instruments-commission');
  const [activeTabId, setActiveTabId] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');

  const tierTabs = useMemo(() => mapPartnerTiersToTabs(tiers), [tiers]);

  const currentName = currentTier?.name || userTier?.currentLevelName || '';
  const currentTabId = useMemo(() => {
    const byName = tierTabs.find(
      (t) => String(t.name).toLowerCase() === String(currentName).toLowerCase()
    );
    if (byName) return byName.id;
    const byLevel = tierTabs.find(
      (t) => t.levelNumber === (currentTier?.levelNumber || userTier?.currentLevel)
    );
    return byLevel?.id || tierTabs[0]?.id || null;
  }, [tierTabs, currentName, currentTier, userTier]);

  useEffect(() => {
    if (!activeTabId && currentTabId) setActiveTabId(currentTabId);
  }, [activeTabId, currentTabId]);

  const selectedId = activeTabId || currentTabId;
  const selectedTab = tierTabs.find((t) => t.id === selectedId) || tierTabs[0];
  const affiliateLevelId = selectedTab?.affiliateLevelId || selectedTab?.id;

  const loadCommissions = useCallback(async (levelId) => {
    if (!levelId) {
      setGroups([]);
      return;
    }
    setGroupsLoading(true);
    setGroupsError('');
    try {
      const res = await apiService.getInstrumentCommissions(levelId);
      if (res?.success) {
        setGroups(mapInstrumentCommissionGroups(res.data));
      } else {
        setGroups([]);
        setGroupsError(res?.error || res?.message || 'Failed to load instrument commissions');
      }
    } catch (err) {
      setGroups([]);
      setGroupsError(err?.message || 'Failed to load instrument commissions');
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommissions(affiliateLevelId);
  }, [affiliateLevelId, loadCommissions]);

  useEffect(() => {
    if (!groups.length) {
      setActiveGroupId(null);
      return;
    }
    setActiveGroupId((prev) => (groups.some((g) => g.id === prev) ? prev : groups[0].id));
  }, [groups]);

  const activeGroup = groups.find((g) => g.id === activeGroupId) || groups[0];
  const standardGroups = groups.filter((g) => g.kind !== 'ultra');
  const ultraGroups = groups.filter((g) => g.kind === 'ultra');

  const filteredPairs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pairs = activeGroup?.pairs || [];
    if (!q) return pairs;
    return pairs.filter((row) => row.symbol.toLowerCase().includes(q));
  }, [activeGroup, search]);

  const avgRate = useMemo(() => {
    const pairs = activeGroup?.pairs || [];
    if (!pairs.length) return 0;
    const sum = pairs.reduce((acc, row) => acc + Number(row.rate || 0), 0);
    return Math.round((sum / pairs.length) * 100) / 100;
  }, [activeGroup]);

  if (loading) {
    return (
      <div className="page ic-page">
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page ic-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={BarChart3}
          title="Instruments Commission"
          subtitle="Compare forex group rates across partner tiers"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error || groupsError} />

        <section className="ic-banner">
          <div className="ic-banner__main">
            <div className="ic-banner__kicker">
              <Layers className="w-3.5 h-3.5" />
              Forex Groups
            </div>
            <h2>Commission rates by tier</h2>
            <p>
              Pick your tier, choose a group, and review instrument commissions in one clean table.
            </p>
          </div>
          <div className="ic-banner__stats">
            <div>
              <span>Your tier</span>
              <strong>{currentName || '—'}</strong>
            </div>
            <div>
              <span>Viewing</span>
              <strong>{selectedTab?.name || '—'}</strong>
            </div>
            <div>
              <span>Groups</span>
              <strong>{groups.length}</strong>
            </div>
          </div>
        </section>

        <section className="ic-forex">
          <div className="ic-tabs" role="tablist" aria-label="Account tiers">
            {tierTabs.map((tab) => {
              const isActive = tab.id === selectedId;
              const isCurrent = tab.id === currentTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`ic-tab${isActive ? ' is-active' : ''}${isCurrent ? ' is-current' : ''}`}
                  style={isActive ? { backgroundColor: THEME, borderColor: THEME } : undefined}
                  onClick={() => setActiveTabId(tab.id)}
                >
                  <span className="ic-tab__name">{tab.name}</span>
                  {isCurrent ? <em className="ic-tab__badge">Current</em> : null}
                </button>
              );
            })}
          </div>

          <div className="ic-forex__panel" role="tabpanel">
            {groupsLoading ? (
              <div className="module-card" style={{ border: 0, boxShadow: 'none' }}>
                <AppLoader color={THEME} />
              </div>
            ) : (
              <div className="ic-layout">
                <aside className="ic-group-nav">
                  {standardGroups.length > 0 ? (
                    <div className="ic-group-nav__section">
                      <div className="ic-group-nav__label">Standard groups</div>
                      {standardGroups.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          className={`ic-group-nav__btn${
                            activeGroup?.id === group.id ? ' is-active' : ''
                          }`}
                          onClick={() => setActiveGroupId(group.id)}
                        >
                          <span className="ic-group-nav__name">{group.name}</span>
                          <span className="ic-group-nav__count">{group.pairs.length}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {ultraGroups.length > 0 ? (
                    <div className="ic-group-nav__section">
                      <div className="ic-group-nav__label">Ultra low standard</div>
                      {ultraGroups.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          className={`ic-group-nav__btn${
                            activeGroup?.id === group.id ? ' is-active' : ''
                          }`}
                          onClick={() => setActiveGroupId(group.id)}
                        >
                          <span className="ic-group-nav__name">{group.name}</span>
                          <span className="ic-group-nav__count">{group.pairs.length}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {!standardGroups.length && !ultraGroups.length ? (
                    <p className="ic-table__empty" style={{ padding: '12px 8px' }}>
                      No instrument groups found for this tier.
                    </p>
                  ) : null}
                </aside>

                <div className="ic-table-wrap">
                  <div className="ic-table-head">
                    <div>
                      <h3>{activeGroup?.name || 'Group'}</h3>
                      <p>
                        {selectedTab?.name} · {activeGroup?.pairs.length || 0} instruments
                      </p>
                    </div>
                    <div className="ic-table-meta">
                      <div className="ic-chip">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Avg {avgRate}
                      </div>
                      {selectedId === currentTabId ? (
                        <div className="ic-chip ic-chip--current">
                          <Sparkles className="w-3.5 h-3.5" />
                          Your tier
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="ic-search">
                    <Search className="w-4 h-4" />
                    <input
                      type="search"
                      placeholder="Search instrument (e.g. EURUSD)"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  <div className="ic-table-scroll">
                    <table className="ic-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Instrument</th>
                          <th>Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPairs.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="ic-table__empty">
                              No instruments match your search.
                            </td>
                          </tr>
                        ) : (
                          filteredPairs.map((row, index) => (
                            <tr key={`${activeGroup.id}-${row.id || row.symbol}`}>
                              <td className="ic-table__idx">{index + 1}</td>
                              <td>
                                <span className="ic-pair">{row.symbol}</span>
                              </td>
                              <td>
                                <strong className="ic-rate">{row.rate}</strong>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default InstrumentsCommission;
